// The Reporting page's model: the month, its social report, and the canvas layout, all read from the URL so a
// refresh, Back and a shared link restore them.
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSocialMediaReport } from "./useSocialMediaReport";
import { growthKeys } from "./useGrowth";
import { postsByDay, type PostDay } from "../utils/dayMarkers";
import { useMediaQuery } from "@/core/http/useMediaQuery";
import { useAppStore } from "@/core/store/useAppStore";
import {
  TIER_MIN_WIDTH,
  TILE_CAP,
  autoPlace,
  countTiles,
  edgesFor as edgesOfLayout,
  panelsOf,
  parse,
  place as placeInLayout,
  remove,
  replace as replaceInLayout,
  serialize,
  tile,
  visibleLayout,
  type CanvasLayout,
  type Edge,
  type Tier,
} from "../canvas/canvasLayout";
import { DEFAULT_PANEL, isPanelId, type PanelId } from "../canvas/panels";
import type { SocialReportState } from "../canvas/ReportPanelById";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export interface ReportingViewModel {
  /** Empty while no client is selected. */
  clientId: string;
  year: number;
  month: number;
  periodLabel: string;
  /** The month's report has a month before to compare with. */
  comparesPrevious: boolean;
  updatedAt: string | null;
  setPeriod(year: number, month: number): void;
  social: SocialReportState;
  /** The social report is in, so its Summary can be written. */
  hasReport: boolean;
  isRefreshing: boolean;
  refreshingPlatform: string | null;
  refreshReport(): void;
  refreshPlatform(platform: "facebook" | "instagram" | "tiktok"): void;
  /** The month's posts by Rome day, the pips on Ad performance's chart. */
  postDays: PostDay[];

  /** The window's width tier: a phone lists the panels, a tablet and a desktop draw the rail and canvas. */
  tier: Tier;
  /** How many tiles this tier shows. */
  cap: number;
  /** The whole canvas, as the URL has it. Null is an empty canvas. */
  layout: CanvasLayout | null;
  /** The part of it this tier shows: all of it, except a tablet's first two tiles of a larger layout. */
  shown: CanvasLayout | null;
  /** Every panel in the layout: expanded on a phone, ringed in the rail. */
  openPanels: PanelId[];
  /** Panels in the layout past this tier's cap, in reading order. */
  hiddenPanels: PanelId[];
  /** There is room for another panel at this tier. */
  canAdd: boolean;
  /** The edges a new tile can land on beside this panel's tile. */
  edgesFor(panel: PanelId): Edge[];
  /** Fills an empty canvas. */
  open(panel: PanelId): void;
  place(panel: PanelId, target: PanelId, edge: Edge): void;
  /** Opens the panel in the next free spot, a desktop's click with no tile picked and a phone's tap alike. */
  add(panel: PanelId): void;
  /** Puts `panel` in `target`'s tile instead, the split unchanged. */
  replace(target: PanelId, panel: PanelId): void;
  close(panel: PanelId): void;
}

/** The query, written by hand so the canvas reads `growth,(website/googleads)` in the address bar:
 *  URLSearchParams would escape every comma, slash and bracket. Ids are [a-z0-9], so nothing needs escaping. */
const searchOf = (fields: Array<[string, string | null]>) => {
  const parts = fields.filter((f): f is [string, string] => f[1] != null).map(([k, v]) => `${k}=${v}`);
  return parts.length ? `?${parts.join("&")}` : "";
};

export function useReportingView(): ReportingViewModel {
  const { selectedClient } = useAppStore();
  const clientId = selectedClient?.id ?? "";
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const now = new Date();
  const year = Number(query.get("year")) || now.getFullYear();
  const monthParam = Number(query.get("month"));
  const month = monthParam >= 1 && monthParam <= 12 ? monthParam : now.getMonth() + 1;

  const desktop = useMediaQuery(`(min-width: ${TIER_MIN_WIDTH.desktop}px)`);
  const tablet = useMediaQuery(`(min-width: ${TIER_MIN_WIDTH.tablet}px)`);
  const tier: Tier = desktop ? "desktop" : tablet ? "tablet" : "phone";
  const cap = TILE_CAP[tier];

  const canvasText = query.get("canvas");
  // Round 1's phone link, `panel=website`, reads as a canvas of that one panel.
  const legacyText = query.get("panel");
  const legacyPanel = legacyText && isPanelId(legacyText) ? legacyText : null;
  const layout = useMemo((): CanvasLayout | null => {
    if (canvasText == null) return tile(legacyPanel ?? DEFAULT_PANEL);
    const parsed = parse(canvasText, isPanelId);
    return parsed === "invalid" ? tile(DEFAULT_PANEL) : parsed;
  }, [canvasText, legacyPanel]);

  // Any write drops `panel`: from then on the canvas carries the layout.
  const go = useCallback(
    (next: { year?: number; month?: number; layout?: CanvasLayout | null }) => {
      const nextLayout = next.layout === undefined ? (canvasText ?? (legacyPanel ? serialize(layout) : null)) : serialize(next.layout);
      navigate({
        search: searchOf([
          ["year", String(next.year ?? year)],
          ["month", String(next.month ?? month)],
          ["canvas", nextLayout],
        ]),
      });
    },
    [navigate, canvasText, legacyPanel, layout, year, month]
  );

  const {
    report, previousReport, comparison, historicalData,
    isLoading, error, isPending, isRefreshing,
    refreshingPlatform, refreshReport, refreshPlatform,
  } = useSocialMediaReport(clientId, year, month);
  // Rate of growth reads the social report's figures, so a refresh of it refetches growth too.
  const refetchGrowth = () => queryClient.invalidateQueries({ queryKey: growthKeys.all(clientId) });

  const periodLabel = `${MONTHS[month - 1]} ${year}`;
  const social = useMemo((): SocialReportState => {
    if (isLoading) return { status: "loading" };
    if (report) return { status: "ready", report, comparison, historicalData };
    if (isPending) {
      return { status: "missing", message: `No social report for ${periodLabel}.`, hint: "Reports are generated nightly. Refresh generates one now." };
    }
    if (error?.includes("No social media accounts connected")) {
      return { status: "missing", message: "No social media connected.", hint: "Connect Facebook or Instagram to see analytics." };
    }
    return { status: "missing", message: "Could not load the social report.", hint: error ?? "" };
  }, [isLoading, report, comparison, historicalData, isPending, error, periodLabel]);

  const postDays = useMemo(() => (report ? postsByDay(report, report.year, report.month) : []), [report]);

  const openPanels = panelsOf(layout).filter(isPanelId);
  const { layout: shown, hidden } = useMemo(() => visibleLayout(layout, cap), [layout, cap]);

  return {
    clientId,
    year,
    month,
    periodLabel,
    comparesPrevious: previousReport != null,
    updatedAt: report?._meta?.updatedAt ?? null,
    setPeriod: (y, m) => go({ year: y, month: m }),
    social,
    hasReport: report != null,
    isRefreshing,
    refreshingPlatform,
    refreshReport: () => {
      void refreshReport().then(refetchGrowth);
    },
    refreshPlatform: (platform) => {
      void refreshPlatform(platform).then(refetchGrowth);
    },
    postDays,

    tier,
    cap,
    layout,
    shown,
    openPanels,
    hiddenPanels: hidden.filter(isPanelId),
    canAdd: countTiles(layout) < cap,
    edgesFor: (panel) => edgesOfLayout(layout, panel, cap),
    open: (panel) => {
      if (layout == null) go({ layout: tile(panel) });
    },
    place: (panel, target, edge) => {
      const result = placeInLayout(layout, panel, { target, edge }, cap);
      if (result.ok) go({ layout: result.layout });
    },
    add: (panel) => {
      const result = autoPlace(layout, panel, cap);
      if (result.ok) go({ layout: result.layout });
    },
    replace: (target, panel) => go({ layout: replaceInLayout(layout, target, panel) }),
    close: (panel) => go({ layout: remove(layout, panel) }),
  };
}
